/**
 * Structured data (schema.org JSON-LD) helpers. Builders return plain objects;
 * the <JsonLd> component serializes them into a script tag. Rendering the
 * script in a (server-rendered) component puts the markup in the initial HTML
 * so crawlers and AI models can parse it without executing JavaScript.
 */

import {
  absoluteUrl,
  FEATURE_LIST,
  FOUNDERS,
  ORG_ADDRESS,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_OG_IMAGE,
  SITE_URL,
} from "./siteConfig";

/** Stable @id anchors so nodes can reference each other within the graph. */
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const SOFTWARE_ID = `${SITE_URL}/#software`;

interface JsonLdProps {
  data: Record<string, unknown>;
}

export function JsonLd({ data }: JsonLdProps) {
  // The payload is built from our own constants, never user input. Escaping "<"
  // additionally prevents any string from breaking out of the script tag.
  const json = JSON.stringify(data).replace(/</g, "\\u003c");

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}

/**
 * Site-wide identity graph: Organization, WebSite, and SoftwareApplication as
 * one @graph so the Organization is defined once and referenced by the others.
 * Emitted once in the root layout.
 */
export function siteGraph(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: SITE_NAME,
        url: SITE_URL,
        description: SITE_DESCRIPTION,
        logo: absoluteUrl("/logo/logo.svg"),
        address: {
          "@type": "PostalAddress",
          ...ORG_ADDRESS,
        },
        founders: FOUNDERS.map((founder) => ({
          "@type": "Person",
          name: founder.name,
          jobTitle: founder.jobTitle,
          sameAs: founder.linkedIn,
        })),
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        url: SITE_URL,
        name: SITE_NAME,
        description: SITE_DESCRIPTION,
        publisher: { "@id": ORG_ID },
      },
      {
        "@type": "SoftwareApplication",
        "@id": SOFTWARE_ID,
        name: SITE_NAME,
        url: SITE_URL,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description: SITE_DESCRIPTION,
        publisher: { "@id": ORG_ID },
        featureList: [...FEATURE_LIST],
      },
    ],
  };
}

interface FaqItem {
  question: string;
  answer: string;
}

/** FAQPage schema for the homepage Q&A. AI models preferentially cite this. */
export function faqPageSchema(items: FaqItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

interface BreadcrumbItem {
  name: string;
  /** Site-relative path (ex: "/platform"). */
  path: string;
}

/** BreadcrumbList schema so AI engines see the site hierarchy for a sub-page. */
export function breadcrumbSchema(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

const ARTICLE_PATH_PREFIX: Record<"guide" | "comparison", string> = {
  guide: "/resources",
  comparison: "/compare",
};

interface ArticleSchemaInput {
  kind: "guide" | "comparison";
  slug: string;
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
}

/**
 * Article schema for a guide or comparison page. Attributed to a Person when
 * `author` is set, otherwise to the Organization. Only emitted on real article
 * pages (resources/compare), never on product or landing pages where it would
 * be invalid structured data.
 */
export function articleSchema(input: ArticleSchemaInput): Record<string, unknown> {
  const path = `${ARTICLE_PATH_PREFIX[input.kind]}/${input.slug}`;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.title,
    description: input.description,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: input.author ? { "@type": "Person", name: input.author } : { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: absoluteUrl(path),
    // Raster 1200x630 card; rich-result guidelines want a real image, not a logo SVG.
    image: absoluteUrl(SITE_OG_IMAGE.url),
  };
}
